import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  TrendingUp,
  Users,
  Target,
  Lightbulb,
  Loader,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { blogApi, ContentSuggestion, ContentAnalysisResult } from '../services/api';

interface ContentSuggestionsPanelProps {
  isVisible: boolean;
  onSuggestionSelect: (suggestion: ContentSuggestion) => void;
  onClose: () => void;
}

export default function ContentSuggestionsPanel({
  isVisible,
  onSuggestionSelect,
  onClose,
}: ContentSuggestionsPanelProps) {
  const [analysis, setAnalysis] = useState<ContentAnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFocusAreas, setSelectedFocusAreas] = useState<string[]>([]);

  const focusAreaOptions = [
    'SEO',
    'Engagement',
    'Thought Leadership',
    'How-to Guides',
    'Case Studies',
    'Industry Trends',
    'Best Practices',
    'Tool Reviews',
  ];

  const loadSuggestions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await blogApi.admin.analyzeAndSuggest({
        analyzeCount: 10,
        suggestionsCount: 6,
        focusAreas: selectedFocusAreas,
      });

      if (response.success && response.data) {
        setAnalysis(response.data);
      } else {
        setError(response.error || 'Failed to load suggestions');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load suggestions');
    } finally {
      setIsLoading(false);
    }
  };

  // Load suggestions when panel becomes visible
  useEffect(() => {
    if (isVisible && !analysis) {
      loadSuggestions();
    }
  }, [isVisible]);

  const handleSuggestionClick = (suggestion: ContentSuggestion) => {
    onSuggestionSelect(suggestion);
    onClose();
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-600 bg-green-100';
    if (confidence >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-orange-600 bg-orange-100';
  };

  const getStyleIcon = (style: string) => {
    switch (style) {
      case 'how-to':
        return '📝';
      case 'listicle':
        return '📋';
      case 'case-study':
        return '📊';
      case 'opinion':
        return '💭';
      case 'news':
        return '📰';
      case 'tutorial':
        return '🎓';
      default:
        return '📄';
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 300 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 300 }}
        className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl border-l border-gray-200 z-50 overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Brain className="h-5 w-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">AI Content Suggestions</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={loadSuggestions}
                disabled={isLoading}
                className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                ×
              </button>
            </div>
          </div>

          {/* Focus Areas */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Focus Areas (optional)
            </label>
            <div className="flex flex-wrap gap-1">
              {focusAreaOptions.map((area) => (
                <button
                  key={area}
                  onClick={() => {
                    setSelectedFocusAreas((prev) =>
                      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
                    );
                  }}
                  className={`px-2 py-1 text-xs rounded-full transition-colors ${
                    selectedFocusAreas.includes(area)
                      ? 'bg-purple-100 text-purple-700 border border-purple-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  {area}
                </button>
              ))}
            </div>
          </div>

          {selectedFocusAreas.length > 0 && (
            <button
              onClick={loadSuggestions}
              disabled={isLoading}
              className="w-full text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Analyzing...' : 'Update Suggestions'}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-3" />
                <p className="text-sm text-gray-600">Analyzing content...</p>
                <p className="text-xs text-gray-500 mt-1">This may take a moment</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={loadSuggestions}
                className="mt-2 text-xs text-red-700 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          )}

          {analysis && !isLoading && (
            <div className="space-y-6">
              {/* Analysis Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Content Analysis
                </h4>
                <div className="text-sm text-blue-700 space-y-2">
                  {analysis.analysis.contentGaps.length > 0 && (
                    <div>
                      <span className="font-medium">Content Gaps:</span>{' '}
                      {analysis.analysis.contentGaps.join(', ')}
                    </div>
                  )}
                  {analysis.analysis.popularKeywords.length > 0 && (
                    <div>
                      <span className="font-medium">Popular Keywords:</span>{' '}
                      {analysis.analysis.popularKeywords.slice(0, 5).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* Suggestions */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <Lightbulb className="h-4 w-4 mr-2 text-yellow-500" />
                  Suggested Content ({analysis.suggestions.length})
                </h4>
                <div className="space-y-3">
                  {analysis.suggestions.map((suggestion) => (
                    <motion.div
                      key={suggestion.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-purple-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getStyleIcon(suggestion.style)}</span>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getConfidenceColor(suggestion.confidence)}`}
                          >
                            {suggestion.confidence}% match
                          </span>
                        </div>
                        <Sparkles className="h-4 w-4 text-purple-400" />
                      </div>

                      <h5 className="font-medium text-gray-900 mb-2 line-clamp-2">
                        {suggestion.title}
                      </h5>

                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {suggestion.description}
                      </p>

                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-2 text-gray-500">
                          <Users className="h-3 w-3" />
                          <span>{suggestion.targetAudience}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Target className="h-3 w-3 text-green-500" />
                          <span className="text-green-600 font-medium capitalize">
                            {suggestion.style}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">
                          <span className="font-medium">Why this works:</span>{' '}
                          {suggestion.reasoning}
                        </p>
                      </div>

                      {suggestion.keywords.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {suggestion.keywords.slice(0, 3).map((keyword) => (
                            <span
                              key={keyword}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md"
                            >
                              {keyword}
                            </span>
                          ))}
                          {suggestion.keywords.length > 3 && (
                            <span className="px-2 py-1 text-xs text-gray-500">
                              +{suggestion.keywords.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
